import { SEO } from "@/components/SEO";
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Target, Eye, Award, Users, Globe, Handshake, GripHorizontal } from "lucide-react";
import teamWorking from "@/assets/team-working.jpg";
import { useLanguage } from "@/contexts/LanguageContext";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";

export default function About() {
  const { t, language } = useLanguage();
  const [showDragHint, setShowDragHint] = useState(false);
  const [hintPosition, setHintPosition] = useState({ x: 0, y: 0 });

  const values = [
    { icon: Target, title: t("about.mission"), description: t("about.missionDesc") },
    { icon: Eye, title: t("about.vision"), description: t("about.visionDesc") },
    { icon: Award, title: t("about.values"), description: t("about.valuesDesc") },
    { icon: Users, title: t("about.team"), description: t("about.teamDesc") },
  ];

  const stats = [
    { value: "20", suffix: "+", label: t("about.newStat1") },
    { value: "600", suffix: "+", label: t("about.newStat2") },
    { value: "7", suffix: "+", label: t("about.newStat3") },
    { value: "100", suffix: "+", label: t("about.newStat4") },
    { value: "50", suffix: "+", label: t("about.newStat5") },
    { value: "2000", suffix: "+", label: t("about.newStat6") },
  ];

  const milestones = [
    { year: "2003", items: [t("about.milestone2003_1"), t("about.milestone2003_2"), t("about.milestone2003_3")] },
    { year: "2008", items: [t("about.milestone2008_1"), t("about.milestone2008_2"), t("about.milestone2008_3")] },
    { year: "2014-2015", items: [t("about.milestone2014_1"), t("about.milestone2014_2")] },
    { year: "2016-2018", items: [t("about.milestone2016_1"), t("about.milestone2016_2"), t("about.milestone2016_3")] },
    { year: "2019-2020", items: [t("about.milestone2019_1"), t("about.milestone2019_2"), t("about.milestone2019_3")] },
    { year: "2022", items: [t("about.milestone2022_1")] },
  ];

  const highlights = [
    { icon: Globe, title: t("about.highlight3Title"), desc: t("about.highlight3Desc") },
    { icon: Handshake, title: t("about.highlight4Title"), desc: t("about.highlight4Desc") },
  ];

  return (
    <div className="min-h-screen bg-muted/50">
      <SEO 
        title="About CargoNova — AI Freight Platform by Deep Insights"
        description="CargoNova is an AI freight platform by Deep Insights (Singapore). Learn about our mission to transform global freight forwarding with AI-native technology."
        canonical="https://cargonova.ai/about"
        titleZh="关于 CargoNova — Deep Insights 旗下 AI 货代平台"
        descriptionZh="CargoNova 是 Deep Insights（新加坡）旗下的 AI 货代平台。了解我们通过 AI Native 技术革新国际货代行业的使命与团队。"
        canonicalZh="https://cargonova.ai/zh/about"
        canonicalTh="https://cargonova.ai/th/about"
      />
      <Navbar />
      
      <main className="pt-20">
        {/* Hero Section */}
        <section className="pt-2 pb-4">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="mb-4"
            >
              <div className="relative rounded-2xl overflow-hidden">
                <img src={teamWorking} alt={t("about.imgAlt")} className="w-full h-56 md:h-72 lg:h-[380px] object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-primary via-primary/40 to-transparent" />
                <div className="absolute bottom-5 left-5 right-5 md:bottom-8 md:left-8">
                  <span className="inline-block px-3 py-1 rounded-full bg-gradient-ai text-primary-foreground text-sm font-medium mb-2 shadow-ai">
                    {t("about.badge")}
                  </span>
                  <h1 className="text-2xl md:text-4xl font-bold">
                    <span className="text-white">{t("about.title1")}</span>
                    <span className="text-gradient-ai">{t("about.title2")}</span>
                    <span className="text-white">{t("about.title3")}</span>
                  </h1>
                </div>
              </div>
              <div className="mt-5 flex items-start sm:items-center justify-between gap-4 px-5 py-4 bg-card/50 rounded-xl border border-border/40 backdrop-blur-xs">
                <div className="flex items-start sm:items-center gap-3">
                  <div className="w-1 h-5 rounded-full bg-secondary shrink-0 mt-0.5 sm:mt-0" />
                  {language === "zh" ? (
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      <span className="font-semibold text-foreground">CargoNova</span> 是{" "}
                      <span className="font-medium text-foreground">Deep Insights</span> 产品矩阵的一员，与{" "}
                      <span className="font-medium text-foreground">WMS Cloud</span> 及{" "}
                      <span className="font-medium text-foreground">TMS Cloud</span> 并肩。
                    </p>
                  ) : language === "th" ? (
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      <span className="font-semibold text-foreground">CargoNova</span> เป็นส่วนหนึ่งของกลุ่มผลิตภัณฑ์{" "}
                      <span className="font-medium text-foreground">Deep Insights</span> ร่วมกับ{" "}
                      <span className="font-medium text-foreground">WMS Cloud</span> และ{" "}
                      <span className="font-medium text-foreground">TMS Cloud</span>
                    </p>
                  ) : (
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      <span className="font-semibold text-foreground">CargoNova</span> is part of the{" "}
                      <span className="font-medium text-foreground">Deep Insights</span> product suite, alongside{" "}
                      <span className="font-medium text-foreground">WMS Cloud</span> and{" "}
                      <span className="font-medium text-foreground">TMS Cloud</span>.
                    </p>
                  )}
                </div>
                <a
                  href="https://www.deep-insights.ai/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="shrink-0 inline-flex items-center gap-1.5 text-sm font-medium text-secondary hover:text-secondary/80 transition-colors whitespace-nowrap group"
                >
                  <span>{language === "zh" ? "了解 Deep Insights 母公司" : language === "th" ? "เรียนรู้เกี่ยวกับ Deep Insights" : "Learn about Deep Insights"}</span>
                  <span className="inline-block transition-transform duration-300 group-hover:translate-x-1">→</span>
                </a>
              </div>
            </motion.div>
          </div>
        </section>


        {/* Company Introduction */}
        <section className="py-3">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 mb-12">
              {/* Left: Company Introduction */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="flex flex-col justify-center"
              >
                <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-6">
                  {t("about.companyIntro")}
                </h2>
                <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                  {t("about.p1")}
                </p>
                <a 
                  href="https://www.deep-insights.ai/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-secondary hover:text-secondary/80 underline transition-colors text-sm font-medium mt-2"
                >
                  {t("about.learnMoreDongyin")}
                </a>
              </motion.div>

              {/* Right: Highlights Grid */}
              <div className="grid grid-cols-1 gap-4">
                {highlights.map((item, index) => (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.1 * index }}
                    className="bg-card rounded-xl p-5 border border-border hover:border-secondary/30 hover:shadow-lg transition-all duration-300"
                  >
                    <div className="w-10 h-10 bg-secondary/10 rounded-lg flex items-center justify-center mb-3">
                      <item.icon className="w-5 h-5 text-secondary" />
                    </div>
                    <h3 className="font-semibold text-foreground mb-1">{item.title}</h3>
                    <p className="text-sm text-muted-foreground">{item.desc}</p>
                  </motion.div>
                ))}
              </div>
            </div>


            {/* Stats Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-card rounded-2xl p-6 md:p-8 border border-border"
            >
              <div className="grid grid-cols-3 md:grid-cols-6 gap-4 md:gap-6">
                {stats.map((stat, index) => (
                  <motion.div
                    key={stat.label}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.05 * index }}
                    className="text-center"
                  >
                    <div className="text-2xl md:text-4xl font-bold text-secondary">
                      {stat.value}
                      <span className="text-secondary/60">{stat.suffix}</span>
                    </div>
                    <div className="text-xs md:text-sm text-muted-foreground mt-2">{stat.label}</div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Timeline Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mt-12"
            >
              <h3 className="text-xl md:text-2xl font-bold text-foreground mb-8">{t("about.timeline")}</h3>
              
              {/* Horizontal Timeline with scroll */}
              <div 
                className="relative overflow-x-auto pb-4 cursor-grab active:cursor-grabbing [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
                onMouseEnter={(e) => {
                  setShowDragHint(true);
                }}
                onMouseLeave={(e) => {
                  setShowDragHint(false);
                  e.currentTarget.dataset['isDragging'] = 'false';
                }}
                onMouseMove={(e) => {
                  const el = e.currentTarget;
                  const rect = el.getBoundingClientRect();
                  const scrollLeft = el.scrollLeft;
                  const mouseXInContainer = e.clientX - rect.left;
                  // Position tooltip relative to mouse, accounting for scroll
                  const tooltipX = Math.min(mouseXInContainer + 15, rect.width - 100);
                  setHintPosition({ x: tooltipX, y: e.clientY - rect.top - 10 });
                  
                  if (el.dataset['isDragging'] !== 'true') return;
                  e.preventDefault();
                  const x = e.pageX - el.offsetLeft;
                  const walk = (x - Number(el.dataset['startX'])) * 1.5;
                  el.scrollLeft = Number(el.dataset['scrollLeft']) - walk;
                }}
                onMouseDown={(e) => {
                  const el = e.currentTarget;
                  el.dataset['isDragging'] = 'true';
                  el.dataset['startX'] = String(e.pageX - el.offsetLeft);
                  el.dataset['scrollLeft'] = String(el.scrollLeft);
                  setShowDragHint(false);
                }}
                onMouseUp={(e) => {
                  e.currentTarget.dataset['isDragging'] = 'false';
                  setShowDragHint(true);
                }}
              >
                {/* Drag Hint Tooltip */}
                <AnimatePresence>
                  {showDragHint && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.8 }}
                      transition={{ duration: 0.15 }}
                      className="absolute z-10 pointer-events-none bg-secondary text-primary-foreground px-3 py-1.5 rounded-lg text-xs font-medium shadow-lg flex items-center gap-1.5"
                      style={{ left: hintPosition.x, top: hintPosition.y }}
                    >
                      <GripHorizontal className="w-3 h-3" />
                      {language === 'zh' ? '拖动浏览' : 'Drag to scroll'}
                    </motion.div>
                  )}
                </AnimatePresence>
                <div className="relative min-w-max px-4 pt-10">
                  {/* Timeline Line */}
                  <div className="absolute top-8 left-0 right-0 h-0.5 bg-gradient-to-r from-secondary/20 via-secondary to-secondary/20" />
                  
                  {/* Timeline Items */}
                  <div className="flex gap-8 md:gap-12">
                    {milestones.map((milestone, index) => (
                      <motion.div
                        key={milestone.year}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.1 * index }}
                        className="relative flex flex-col w-48 md:w-56 pt-6"
                      >
                        {/* Timeline Dot - on the line */}
                        <div className="absolute -top-4 left-0 w-3 h-3 bg-secondary rounded-full border-2 border-background shadow-lg" />
                        
                        {/* Year */}
                        <div className="text-lg md:text-xl font-bold text-secondary mb-3">{milestone.year}</div>
                        
                        {/* Items */}
                        <ul className="space-y-1.5">
                          {milestone.items.map((item, idx) => (
                            <li key={idx} className="text-xs text-muted-foreground leading-relaxed flex items-start gap-1.5">
                              <span className="text-secondary mt-0.5 shrink-0">·</span>
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>



      </main>

      <Footer />
    </div>
  );
}